import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, KeyRound, Smartphone, Clock, LogOut, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function tokenExpSeconds() {
  try {
    const t = localStorage.getItem("token") || "";
    const exp = JSON.parse(atob(t.split(".")[1])).exp;
    return Math.max(0, Math.floor(exp - Date.now() / 1000));
  } catch {
    return 900;
  }
}

export default function SecuritySetup() {
  const [left, setLeft] = useState(tokenExpSeconds());
  const [busy, setBusy] = useState(false);
  const [totpData, setTotpData] = useState(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);

  useEffect(() => {
    const iv = setInterval(() => setLeft(s => {
      if (s <= 1) { localStorage.removeItem("token"); window.location.href = "/login"; }
      return s - 1;
    }), 1000);
    return () => clearInterval(iv);
  }, []);

  const finish = (newToken) => {
    localStorage.setItem("token", newToken);
    toast.success("Security setup complete. Welcome!");
    setTimeout(() => { window.location.href = "/"; }, backupCodes ? 0 : 800);
  };

  const addPasskey = async () => {
    setBusy(true);
    try {
      const { startRegistration } = await import("@simplewebauthn/browser");
      const { data: opts } = await axios.post(`${API}/auth/passkey/registration/options`);
      const attResp = await startRegistration({ optionsJSON: opts });
      const { data } = await axios.post(`${API}/auth/passkey/registration/verify`, attResp);
      if (data.access_token) finish(data.access_token);
      else toast.success("Passkey added");
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Passkey setup failed or was cancelled");
    } finally { setBusy(false); }
  };

  const startTotp = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/setup`);
      setTotpData(data);
    } catch { toast.error("Could not start 2FA setup"); }
    finally { setBusy(false); }
  };

  const verifyTotp = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/verify`, { code });
      setBackupCodes(data.backup_codes || []);
      if (data.access_token) {
        localStorage.setItem("token", data.access_token);
        toast.success("2FA enabled — save your backup codes, then continue");
      }
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Invalid code");
    } finally { setBusy(false); }
  };

  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6" data-testid="security-setup-page">
      <div className="w-full max-w-2xl space-y-5">
        <div className="text-center space-y-2">
          <ShieldCheck className="w-10 h-10 text-blue-500 mx-auto" />
          <h1 className="text-2xl font-bold text-white font-mono">Secure Your Account</h1>
          <p className="text-zinc-400 text-sm">
            This app controls live servers. You must add a passkey or an authenticator app before continuing.
          </p>
          <div className="inline-flex items-center gap-2 text-amber-400 text-sm font-mono bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-1" data-testid="enrollment-countdown">
            <Clock className="w-4 h-4" /> Session expires in {mm}:{ss}
          </div>
        </div>

        {backupCodes ? (
          <Card className="bg-[#0c0c0e] border-white/5">
            <CardHeader>
              <CardTitle className="text-white text-base">Save your backup codes</CardTitle>
              <CardDescription>Each works once if you lose your authenticator.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-2 font-mono text-sm text-zinc-300" data-testid="backup-codes-list">
                {backupCodes.map(c => <div key={c} className="bg-black/40 rounded px-3 py-1.5">{c}</div>)}
              </div>
              <Button onClick={() => { window.location.href = "/"; }} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="enrollment-continue-btn">
                I saved them — continue to the app
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="bg-[#0c0c0e] border-white/5">
              <CardHeader>
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <KeyRound className="w-4 h-4 text-blue-400" /> Passkey <span className="text-[10px] text-emerald-400 bg-emerald-500/10 rounded-full px-2 py-0.5">Recommended</span>
                </CardTitle>
                <CardDescription>Fingerprint / Face ID / Windows Hello. Phishing-proof.</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={addPasskey} disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="enroll-passkey-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add a passkey"}
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-[#0c0c0e] border-white/5">
              <CardHeader>
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-purple-400" /> Authenticator app
                </CardTitle>
                <CardDescription>Google Authenticator, Authy, 1Password…</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {!totpData ? (
                  <Button onClick={startTotp} disabled={busy} variant="outline" className="w-full border-white/10 text-zinc-200 hover:bg-white/5" data-testid="enroll-totp-btn">
                    Set up authenticator
                  </Button>
                ) : (
                  <>
                    <img src={`data:image/png;base64,${totpData.qr_code}`} alt="QR" className="w-36 h-36 mx-auto rounded bg-white p-1" data-testid="totp-qr-img" />
                    <p className="text-[11px] text-zinc-500 text-center font-mono break-all">or key: {totpData.secret}</p>
                    <div className="flex gap-2">
                      <Input value={code} onChange={e => setCode(e.target.value)} placeholder="6-digit code" maxLength={6}
                             className="bg-black/50 border-white/10 text-white text-center font-mono" data-testid="totp-code-input" />
                      <Button onClick={verifyTotp} disabled={busy || code.length !== 6} className="bg-purple-600 hover:bg-purple-700" data-testid="totp-verify-btn">Verify</Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        <button onClick={() => { localStorage.removeItem("token"); window.location.href = "/login"; }}
                className="mx-auto flex items-center gap-1.5 text-zinc-500 hover:text-zinc-300 text-xs transition-colors" data-testid="enrollment-logout-btn">
          <LogOut className="w-3 h-3" /> Sign out instead
        </button>
      </div>
    </div>
  );
}
